import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MarketingPage } from '@/lib/api/marketing-report-service';

interface Props { pages: MarketingPage[]; }

/**
 * Top pages, with the same metric columns as Traffic Sources so the two tables read
 * consistently: Sessions / Engaged / Eng. Rate, plus Pageviews which is page-specific.
 *
 * One row per GA4 page path — locale variants are not merged. Merging summed sessions
 * across /en, /es and the bare path, which double-counted any session that visited
 * more than one locale and forced an "approximate" marker. Per-path rows are exact and
 * show locale differences the merge hid.
 *
 * Every path is listed — no top-N cap. The long tail is the useful part: pages with 1
 * pageview are the ones worth knowing are not landing.
 *
 * The path column is width-capped and truncates (full value on hover), numbers never
 * wrap, and the table scrolls horizontally rather than pushing the card out of its
 * container.
 */
export function MarketingPagesTable({ pages }: Props) {
  const totalPageviews = pages.reduce((a, p) => a + p.pageviews, 0);
  const totalSessions = pages.reduce((a, p) => a + p.sessions, 0);
  const totalEngaged = pages.reduce((a, p) => a + p.engagedSessions, 0);
  if (pages.length === 0) {
    return (
      <Card>
        <CardHeader><CardTitle>Top Pages</CardTitle></CardHeader>
        <CardContent><p className="text-sm text-muted-foreground">No data for this range.</p></CardContent>
      </Card>
    );
  }

  return (
    <Card className="min-w-0">
      <CardHeader><CardTitle>Top Pages</CardTitle></CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">Page</th>
              <th className="px-2 py-2 text-right font-medium text-muted-foreground whitespace-nowrap">Pageviews</th>
              <th className="px-2 py-2 text-right font-medium text-muted-foreground whitespace-nowrap">Sessions</th>
              <th className="px-2 py-2 text-right font-medium text-muted-foreground whitespace-nowrap">Engaged</th>
              <th className="px-3 py-2 text-right font-medium text-muted-foreground whitespace-nowrap">Eng. Rate</th>
            </tr>
          </thead>
          <tbody>
            {pages.map((p, i) => {
              const rate = p.sessions > 0 ? Math.round(100 * p.engagedSessions / p.sessions) : 0;
              return (
                <tr key={i} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="px-3 py-2 max-w-[200px]">
                    <span className="block truncate font-medium" title={p.path}>{p.path}</span>
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap">{p.pageviews.toLocaleString()}</td>
                  <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap">{p.sessions.toLocaleString()}</td>
                  <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap">{p.engagedSessions.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{rate}%</td>
                </tr>
              );
            })}
          </tbody>
          {/* Weighted engagement rate (total ÷ total), not a mean of the row rates.
              NB these are per-page column totals: a session spanning several pages is
              counted in each, so the session total exceeds the site's session count. */}
          <tfoot>
            <tr className="border-t-2 font-medium">
              <td className="px-3 py-2">Total <span className="text-xs font-normal text-muted-foreground">(all {pages.length} pages)</span></td>
              <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap">{totalPageviews.toLocaleString()}</td>
              <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap">{totalSessions.toLocaleString()}</td>
              <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap">{totalEngaged.toLocaleString()}</td>
              <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                {totalSessions > 0 ? `${Math.round((1000 * totalEngaged) / totalSessions) / 10}%` : '—'}
              </td>
            </tr>
          </tfoot>
        </table>
      </CardContent>
    </Card>
  );
}
